import React, { useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const ShiftReport = ({ shiftHistory, onShiftClosed }) => {
  const { isAdmin } = useAuth();
  const [closingShift, setClosingShift] = useState(null);
  const [endingCash, setEndingCash] = useState('0');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  // Otwarcie modala force-close: zapamietaj klikieta zmiane i wstepnie wypelnij
  // saldo koncowe oczekiwana gotowka kasa-bank (Opcja B) z /shifts/cash-status
  // dla LOKALIZACJI tej zmiany. Gdy nie da sie ustalic - pole zostaje puste,
  // aby admin swiadomie wpisal kwote (nie podstawiamy salda poczatkowego).
  const openCloseModal = async (shift) => {
    setClosingShift(shift);
    setEndingCash('');
    setNotes('');
    if (shift.location_id == null) return;
    try {
      const resp = await api.get('/shifts/cash-status', {
        params: { location_id: shift.location_id, date: shift.data_zmiany, shift_id: shift.id },
      });
      const expected = resp.data?.data?.expected_drawer_cash;
      if (expected != null && !Number.isNaN(parseFloat(expected))) {
        setEndingCash(String(parseFloat(expected).toFixed(2)));
      }
    } catch (e) {
      // Brak danych kasa-bank: zostaw puste, wymagaj swiadomego wpisania.
    }
  };

  const handleForceClose = async () => {
    if (!closingShift) return;
    setLoading(true);
    try {
      const response = await api.post('/shifts/close', {
        // Identyfikuj KONKRETNA klikieta zmiane (id + lokalizacja), aby backend
        // zamknal wlasnie ja, a nie najnowsza otwarta zmiane tego kasjera.
        shift_id: closingShift.id,
        location_id: closingShift.location_id,
        cashier: closingShift.kasjer_login,
        ending_cash: parseFloat(endingCash) || 0,
        notes: notes || `Zamknieta przez admina ${new Date().toLocaleDateString('pl-PL')}`,
      });
      if (response.data?.success) {
        toast.success(`Zmiana ${closingShift.kasjer_login} zamknieta`);
        setClosingShift(null);
        setEndingCash('');
        setNotes('');
        if (onShiftClosed) await onShiftClosed();
      } else {
        toast.error(response.data?.error || 'Blad zamykania zmiany');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Blad zamykania zmiany');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: '0.375rem', backgroundColor: 'var(--card-bg)' }}>
      <div style={{ padding: '0.5rem', backgroundColor: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
        <h5 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600' }}>📋 Historia zmian kasowych</h5>
      </div>
      <div style={{ padding: '0.75rem' }}>
        {shiftHistory.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
              <thead>
                <tr>
                  <th style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)', textAlign: 'left', fontSize: '0.7rem' }}>Data</th>
                  <th style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)', textAlign: 'left', fontSize: '0.7rem' }}>Kasjer</th>
                  <th style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)', textAlign: 'left', fontSize: '0.7rem' }}>Start</th>
                  <th style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)', textAlign: 'left', fontSize: '0.7rem' }}>Koniec</th>
                  <th style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)', textAlign: 'left', fontSize: '0.7rem' }}>Saldo pocz.</th>
                  <th style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)', textAlign: 'left', fontSize: '0.7rem' }}>Saldo końc.</th>
                  <th style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)', textAlign: 'left', fontSize: '0.7rem' }}>Różnica</th>
                  <th style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)', textAlign: 'left', fontSize: '0.7rem' }}>Status</th>
                  {isAdmin && (
                    <th style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)', textAlign: 'center', fontSize: '0.7rem' }}>Akcja</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {shiftHistory.map((shift) => {
                  const formatTime = (time) => {
                    if (!time) return '-';
                    const parts = time.split(':');
                    return `${parts[0]}:${parts[1]}`;
                  };
                  const toMinutes = (time) => {
                    if (!time) return null;
                    const [h, m] = time.split(':');
                    return parseInt(h, 10) * 60 + parseInt(m, 10);
                  };
                  const roznica = parseFloat(shift.roznica || 0);
                  const isOpen = shift.status !== 'zamknieta';
                  // Zmiana przez polnoc: czas_zakonczenia (sam czas, bez daty) jest
                  // wczesniejszy niz czas_rozpoczecia -> zamknieto nastepnego dnia.
                  const startMin = toMinutes(shift.czas_rozpoczecia);
                  const endMin = toMinutes(shift.czas_zakonczenia);
                  const crossesMidnight = shift.status === 'zamknieta'
                    && startMin != null && endMin != null && endMin < startMin;
                  return (
                    <tr key={shift.id}>
                      <td style={{ padding: '0.3rem', borderBottom: '1px solid var(--bg)' }}>{shift.data_zmiany || '-'}</td>
                      <td style={{ padding: '0.3rem', borderBottom: '1px solid var(--bg)' }}>{shift.kasjer_login}</td>
                      <td style={{ padding: '0.3rem', borderBottom: '1px solid var(--bg)' }}>{formatTime(shift.czas_rozpoczecia)}</td>
                      <td style={{ padding: '0.3rem', borderBottom: '1px solid var(--bg)' }}>
                        {formatTime(shift.czas_zakonczenia)}
                        {crossesMidnight && (
                          <span style={{ color: 'var(--text-muted)', fontSize: '0.65rem', marginLeft: '0.2rem' }} title="Zamknieta nastepnego dnia">+1 dzień</span>
                        )}
                      </td>
                      <td style={{ padding: '0.3rem', borderBottom: '1px solid var(--bg)' }}>{parseFloat(shift.saldo_poczatkowe || 0).toFixed(2)} zł</td>
                      <td style={{ padding: '0.3rem', borderBottom: '1px solid var(--bg)' }}>{shift.status === 'zamknieta' ? `${parseFloat(shift.saldo_koncowe || 0).toFixed(2)} zł` : '-'}</td>
                      <td style={{ padding: '0.3rem', borderBottom: '1px solid var(--bg)' }}>
                        {shift.status === 'zamknieta' ?
                          <span style={{ color: roznica >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                            {roznica.toFixed(2)} zł
                          </span> : '-'
                        }
                      </td>
                      <td style={{ padding: '0.3rem', borderBottom: '1px solid var(--bg)' }}>
                        <span style={{
                          backgroundColor: shift.status === 'zamknieta' ? 'var(--success)' : 'var(--warning)',
                          color: '#fff' ,
                          padding: '0.15rem 0.3rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.65rem'
                        }}>
                          {shift.status === 'zamknieta' ? 'Zamknięta' : 'Otwarta'}
                        </span>
                      </td>
                      {isAdmin && (
                        <td style={{ padding: '0.3rem', borderBottom: '1px solid var(--bg)', textAlign: 'center' }}>
                          {isOpen ? (
                            <button
                              onClick={() => openCloseModal(shift)}
                              style={{
                                padding: '4px 10px',
                                fontSize: '11px',
                                backgroundColor: 'var(--danger)',
                                color: '#fff',
                                border: 'none',
                                cursor: 'pointer',
                              }}
                              title="Zamknij zmianę (admin)"
                            >
                              🔒 Zamknij
                            </button>
                          ) : (
                            <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', fontSize: '0.875rem', color: 'var(--text-muted)' }}>Brak danych</div>
        )}
      </div>

      {/* Modal force-close */}
      {closingShift && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'var(--card-bg)', padding: '1.5rem',
            borderRadius: '0.5rem', minWidth: '400px', maxWidth: '500px',
            border: '1px solid var(--border)'
          }}>
            <h4 style={{ marginTop: 0 }}>🔒 Zamknij zmianę (admin)</h4>
            <div style={{ marginBottom: '0.75rem', fontSize: '0.85rem' }}>
              <strong>Kasjer:</strong> {closingShift.kasjer_login}<br />
              <strong>Data:</strong> {closingShift.data_zmiany} {closingShift.czas_rozpoczecia}<br />
              <strong>Saldo początkowe:</strong> {parseFloat(closingShift.saldo_poczatkowe || 0).toFixed(2)} zł
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem', fontWeight: '600' }}>
                Saldo końcowe (gotówka w kasie):
              </label>
              <input
                type="number"
                step="0.01"
                value={endingCash}
                onChange={e => setEndingCash(e.target.value)}
                style={{
                  width: '100%', padding: '0.4rem', fontSize: '0.85rem',
                  border: '1px solid var(--border)', borderRadius: '0.25rem'
                }}
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem', fontWeight: '600' }}>
                Uwagi (opcjonalnie):
              </label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Powod zamykania przez admina..."
                rows={2}
                style={{
                  width: '100%', padding: '0.4rem', fontSize: '0.85rem',
                  border: '1px solid var(--border)', borderRadius: '0.25rem',
                  resize: 'vertical'
                }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                onClick={() => { setClosingShift(null); setEndingCash(''); setNotes(''); }}
                disabled={loading}
                style={{
                  padding: '6px 12px', fontSize: '12px',
                  backgroundColor: 'var(--card-bg)', color: 'var(--text)',
                  border: '1px solid var(--border)',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                Anuluj
              </button>
              <button
                onClick={handleForceClose}
                disabled={loading}
                style={{
                  padding: '6px 12px', fontSize: '12px',
                  backgroundColor: 'var(--danger)', color: '#fff',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? 'Zamykanie...' : '🔒 Zamknij zmianę'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftReport;
